import React, {useState} from "react";
import {Box, IconButton, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {Check, Copy, Mail, Phone} from "lucide-react";
import {getCarrierSupportContact} from "./trackingConfig";
import {LABEL_DOWNLOAD_URL_PREFIX} from "../../Utils/apiUrl";

const CopyInfoCard = ({label, value}) => {
    const theme = useTheme();
    const [isCopied, setIsCopied] = useState(false);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setIsCopied(true);
            window.setTimeout(() => setIsCopied(false), 1400);
        } catch (error) {
            console.warn("Unable to copy tracking value", error);
        }
    };
    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            border: 1,
            borderColor: "divider",
            backgroundColor: "background.default",
            borderRadius: `${theme.shape.lg}px`,
            px: 1.5,
            py: 1.125
        }}>
            <Box sx={{minWidth: 0}}><Typography variant="caption" sx={{
                color: "neutral.500",
                fontWeight: 700,
                display: "block"
            }}>{label}</Typography><Typography variant="subtitle1"
                                               sx={{wordBreak: "break-word"}}>{value}</Typography></Box>
            {value ? <IconButton type="button" aria-label={`Copy ${label}`} onClick={handleCopy}
                                 sx={{width: 44, height: 44}}>{isCopied ?
                <Check size={16} color={theme.palette.success.main}/> : <Copy size={16}/>}</IconButton> : null}
        </Box>
    );
};

export const TrackingCarrierDetails = ({result}) => {
    const theme = useTheme();
    const contact = getCarrierSupportContact(result.carrier);
    return (
        <>
            <Box sx={{
                display: "grid",
                gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"},
                gap: {xs: 0.75, sm: 1.25},
                pt: {xs: 1.5, sm: 2.5}
            }}>
                {result.carrierLogo ? <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    border: 1,
                    borderColor: "divider",
                    backgroundColor: "background.default",
                    borderRadius: `${theme.shape.lg}px`,
                    px: 1.5,
                    py: 1.125
                }}><Box><Typography variant="caption" sx={{
                    color: "neutral.500",
                    fontWeight: 700,
                    display: "block"
                }}>CARRIER</Typography><Typography variant="subtitle1">{result.carrier}</Typography></Box><Box
                    component="img" src={`${LABEL_DOWNLOAD_URL_PREFIX}${result.carrierLogo}`}
                    alt={`${result.carrier} logo`} sx={{height: 40, maxWidth: 96, objectFit: "contain"}}/></Box> : null}
                {
                    result?.shipmentTypeId === 1 ?
                        <>
                            <CopyInfoCard label="BOL#" value={result.bolNumber}/>
                            <CopyInfoCard label="PRO#" value={result.proNumber}/>
                        </>
                        :
                        <CopyInfoCard label="Tracking#" value={result.trackingNumber}/>
                }
            </Box>
            {(result?.email?.length > 0 || result?.phone?.length > 0) ? (
                <Box sx={{
                    display: {xs: "grid", sm: "flex"},
                    gridTemplateColumns: {xs: "1fr", sm: "none"},
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: {xs: 0.75, sm: 2.5},
                    minHeight: 32,
                    mt: {xs: 1.25, sm: 1.5},
                    pt: 1.25,
                    borderTop: 1,
                    borderColor: "divider"
                }}>
                    <Typography variant="caption" sx={{color: "neutral.500", fontWeight: 700, lineHeight: "20px"}}>
                        {result.carrier} SUPPORT
                    </Typography>
                    {
                        result?.phone?.length > 0 &&
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                            minHeight: {xs: 28, sm: 20}
                        }}>
                            <Phone size={14} color={theme.palette.neutral[500]} aria-hidden="true"
                                   style={{display: "block", flexShrink: 0}}/>
                            <Typography component="a" href={`tel:${result?.phone?.[0]?.replace(/[^\d+]/g, "")}`}
                                        variant="subtitle2" sx={{color: "text.primary", lineHeight: "20px"}}>
                                {result?.phone?.[0]}
                            </Typography>
                        </Box>
                    }
                    {
                        result?.email?.length > 0 &&
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                            minHeight: {xs: 28, sm: 20}
                        }}>
                            <Mail size={14} color={theme.palette.neutral[500]} aria-hidden="true"
                                  style={{display: "block", flexShrink: 0}}/>
                            <Typography component="a" href={`mailto:${result?.email?.[0]}`} variant="subtitle2" sx={{
                                minWidth: 0,
                                color: "text.primary",
                                lineHeight: "20px",
                                overflowWrap: "anywhere"
                            }}>
                                {result?.email?.[0]}
                            </Typography>
                        </Box>
                    }
                </Box>
            ) : null}
        </>
    );
};
